import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';

import { PageInfoService } from '../page-info.service';
import { PageInfo } from '../page-info';
import { AuthenticationService } from '../../authentication/authentication.service';
import { UserClaimTypesEnum } from '../../authentication/user-claim-types.enum';

/**
 * This component encapsulates the page header.
 */
@Component({
	selector: 'cc-page-header',
	templateUrl: './page-header.component.html',
	styleUrls: ['./page-header.component.css']
})
export class PageHeaderComponent implements OnInit, OnDestroy {

// #region [ Fields ]

	/** The page title to show in the header. */
	public PageTitle: string = "";

	/** The page description to show in the header. */
	public PageDescription: string = "";

	/** Indicates if the help dialog should be displayed. */
	public ShowHelpDialog: boolean = false;

	/** The subscription for the router service. */
	private _RouterSubscription: Subscription;
	
// #region [ Constructor ]

	constructor(
		private _Router: Router,
		private _PageInfoService: PageInfoService,
		private _AuthenticationService: AuthenticationService
	) {
		this._RouterSubscription = this._Router.events.subscribe(objEvent => this.OnRoute(objEvent));
	}

// #region [ Properties ]

	/**
	 * Indicates if the user panel should be shown.
	 */
	public get ShowUserPanel(): boolean {
		return this._PageInfoService.ShowHeaderUserPanel;
	}

	/**
	 * Indicates if the help panel should be shown.
	 */
	public get ShowHelpPanel(): boolean {
		return this._PageInfoService.ShowHeaderHelpPanel;
	}

	/**
	 * Used for displaying the user name in the view.
	 */
	public get FullName(): string {
		let strFirstName: string, strLastName: string, strName: string;
		
		if (this._AuthenticationService.UserClaims == null) {
			this._AuthenticationService.GetUserClaims();
			strName = null;
		}
		else {
			strFirstName = this._AuthenticationService.GetUserClaimValue(UserClaimTypesEnum.FirstName);
			strLastName = this._AuthenticationService.GetUserClaimValue(UserClaimTypesEnum.LastName);
			strName = (strFirstName == null ? "" : strFirstName);
			if (strName != "")
				strName += " ";
			strName += (strLastName == null ? "" : strLastName);
		}
		
		return strName;
	}

	/**
	 * Gets an object that specifies which CSS class names should be used for the title text.
	 *
	 * @returns A JSON object with the CSS class names to use.
	 * @see ngClass
	 */
	public get GetTitleTextClassNames(): object {
		return {
			"CenteredText": (this.PageDescription == "")
		};
	}

// #region [ Implemented Methods ]

	/**
	 * Component initialization.
	 */
	public ngOnInit(): void {
		let objSubscribe: Subscription;

		// Subscribe to the page info service and save the subscription for later.
		objSubscribe = this._PageInfoService.GetPageInfoObserver().subscribe(
			(objInfo: PageInfo) => this.UpdatePageInfo(objInfo)
		);
	}

	public ngOnDestroy(): void {
		this._RouterSubscription.unsubscribe();
	}

// #region [ Event Handlers ]

	/**
	 * Opens the feedback dialog.
	 */
	public OpenHelpDialog(): void {
		this.ShowHelpDialog = true;
	}

	/**
	 * Closes the feedback dialog.
	 */
	public OnCloseHelpDialog(): void {
		this.ShowHelpDialog = false;
	}

	/**
	 * Checks if the event type is a navigation end and resets the view if it is.
	 *
	 * @param Event The event fired by the navigation event.
	 */
	private OnRoute(Event: object): void {
		if (Event instanceof NavigationEnd) {
			this._PageInfoService.ShowHeaderHelpPanel = this._PageInfoService.ShowHeaderUserPanel = true;
			this.ShowHelpDialog = false;
		}
	}

// #region [ Private Methods ]

	/**
	 * Updates the models used to display the current page information.
	 * 
	 * @param  Info The data for the skin elements of the page.
	 */
	private UpdatePageInfo(Info: PageInfo): void {
		this.PageTitle = Info.PageTitle;
		this.PageDescription = Info.PageDescription;
	}

}